import { GeckoColorScaleThreshold } from 'types'

import { DEFAULT_MISSING_COLOR } from '../heatmapCanvas/constants'
import {
  HEATMAP_BOTTOM_PADDING,
  HEATMAP_HORIZONTAL_PADDING,
  HEATMAP_LEFT_PADDING,
  HEATMAP_TOP_PADDING,
  LEGEND_HEIGHT_PX,
  LEGEND_WIDTH_PX,
} from './constants'
import {
  convertValuesToDisplayUnit,
  createColorScaleInfo,
  createDisplayBounds,
  cropValues,
  resolveAxisDisplayUnit,
  resolveValueDisplayUnit,
} from './data'
import {
  getBoilerTubeAxisTicks,
  getCustomerTubeNumber,
} from './boilerTubeNumbering'
import { createReportHeatmapDescriptor } from './descriptor'
import { getRightGutterWidth, renderReportOverlayCanvas } from './overlayLayout'
import { buildReportOverlayPrimitives } from './overlayPrimitives'
import { getCartesianPhysicalAspectRatio } from './physicalBinGeometry'
import {
  getCompassLabels,
  getCompassOrientationDegrees,
  hasFullPolarSweep,
  isPolarCircularHeatmap,
  polarToCanvasPoint,
  toCanvasAngleRadians,
  toDegrees,
} from './polarGeometry'
import {
  createTicksWithEndpoints,
  formatAxisTick,
  formatTick,
  selectNonOverlappingTickIndices,
} from './ticks'
import { formatAxisTitle } from './format'
import type {
  ColorScaleInfo,
  CroppedValues,
  NormalizedBounds,
  PlotMetrics,
  ReportHeatmapBundle,
  ReportHeatmapRequest,
} from './types'
import { ReportOverlayMode as OverlayMode } from './types'
import { wrapCanvasText } from './utils'

const POLAR_PLOT_HORIZONTAL_PADDING = 72
const POLAR_PLOT_VERTICAL_PADDING = 72
const POLAR_COMPASS_TICK_INSET_PX = 4
const POLAR_COMPASS_TICK_LENGTH_PX = 8
const POLAR_COMPASS_LABEL_OFFSET_PX = 24
const BASE_AXIS_OUTPUT_WIDTH_PX = 667
const BASE_AXIS_FONT_SIZE_PX = 12
const MAX_AXIS_FONT_SIZE_PX = 36
const MAX_AXIS_LEFT_PADDING_PX = 100
const MAX_AXIS_BOTTOM_PADDING_PX = 115
const MAX_AXIS_RIGHT_PADDING_PX = 48
const AXIS_EDGE_PADDING_PX = 5

type CartesianAxisLayout = {
  fontSize: number
  topPadding: number
  bottomPadding: number
  leftPadding: number
  horizontalPadding: number
}

export async function renderReportHeatmapBundle(
  request: ReportHeatmapRequest
): Promise<ReportHeatmapBundle> {
  const descriptor = createReportHeatmapDescriptor(request)
  // Convert the full dataset to display units once and crop from the
  // converted buffer; elementwise conversion commutes with cropping. The
  // color scale must derive from the full dataset rather than the cropped
  // section values, so that every image rendered from the same CDF (the
  // component overview and each of its sections) shares one scale and legend.
  const valueDisplayUnit =
    request.valueDisplayUnit ??
    resolveValueDisplayUnit(request.cdf.data.params.measurement_unit)
  const displayRawValues = convertValuesToDisplayUnit(
    request.rawValues,
    request.cdf.data.params.measurement_unit,
    valueDisplayUnit
  )
  const croppedValues = cropValues(
    request.cdf,
    request.bounds,
    displayRawValues,
    request.rawValuesXBinCount
  )
  const displayValues = croppedValues.values
  const colorScale = createColorScaleInfo(
    request.cdf,
    request.colorThreshold as GeckoColorScaleThreshold | undefined,
    displayRawValues,
    valueDisplayUnit
  )
  const heatmapCanvas = renderHeatmapCanvas(
    request,
    croppedValues,
    displayValues,
    colorScale
  )
  const legendCanvas = renderLegendCanvas(request, colorScale)
  const heatmapBlob = await canvasToBlob(heatmapCanvas)
  const legendBlob = await canvasToBlob(legendCanvas)

  return {
    heatmapBlob,
    legendBlob,
    descriptor,
  }
}

function renderHeatmapCanvas(
  request: ReportHeatmapRequest,
  croppedValues: CroppedValues,
  displayValues: Float32Array,
  colorScale: ColorScaleInfo
) {
  const canvas = createHtmlCanvas()
  const ctx = canvas.getContext('2d')

  if (!ctx) {
    throw new Error('Failed to create report heatmap canvas context')
  }

  const renderPolarCircular = isPolarCircularHeatmap(
    request.componentModel,
    croppedValues.bounds
  )
  const rightGutterWidth = renderPolarCircular
    ? 0
    : getRightGutterWidth(ctx, request, croppedValues.bounds)

  let plotMetrics: PlotMetrics
  if (renderPolarCircular) {
    canvas.width = request.outputWidthPx
    const plotDiameter = Math.max(
      48,
      request.outputWidthPx - POLAR_PLOT_HORIZONTAL_PADDING * 2
    )
    canvas.height = plotDiameter + POLAR_PLOT_VERTICAL_PADDING * 2
    plotMetrics = createPolarPlotMetrics(canvas, plotDiameter)
  } else {
    const initialAxisLayout = createCartesianAxisLayout(
      request.outputWidthPx,
      request.includeAxes,
      ctx
    )
    const plotWidth = Math.max(
      1,
      request.outputWidthPx -
        initialAxisLayout.leftPadding -
        initialAxisLayout.horizontalPadding -
        rightGutterWidth
    )
    const aspectRatio = getCartesianPhysicalAspectRatio(
      request.componentModel,
      request.cdf,
      croppedValues
    )
    const plotHeight = Math.max(48, Math.round(plotWidth * aspectRatio))
    const axisLayout = addCartesianTitlePadding(
      initialAxisLayout,
      plotHeight,
      ctx,
      request
    )

    canvas.width = request.outputWidthPx
    canvas.height =
      axisLayout.topPadding + plotHeight + axisLayout.bottomPadding
    plotMetrics = createPlotMetrics(canvas, rightGutterWidth, axisLayout)
  }

  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  if (request.includeHeatmap !== false) {
    if (renderPolarCircular) {
      drawPolarCircularCells(
        ctx,
        croppedValues,
        displayValues,
        colorScale,
        plotMetrics
      )
    } else {
      drawCartesianCells(
        ctx,
        croppedValues,
        displayValues,
        colorScale,
        plotMetrics
      )
    }
  }

  if (
    !renderPolarCircular &&
    request.includeOverlay &&
    request.overlayMode === OverlayMode.Embedded
  ) {
    const overlayCanvas = renderOverlayCanvas(
      request,
      croppedValues.bounds,
      plotMetrics
    )
    if (overlayCanvas) {
      ctx.drawImage(overlayCanvas, 0, 0)
    }
  }

  if (request.includeAxes) {
    if (renderPolarCircular) {
      drawPolarCircularAxes(ctx, plotMetrics, request, croppedValues.bounds)
    } else {
      drawAxes(ctx, plotMetrics, croppedValues.bounds, request)
    }
  }

  return canvas
}

function drawCartesianCells(
  ctx: CanvasRenderingContext2D,
  croppedValues: CroppedValues,
  displayValues: Float32Array,
  colorScale: ColorScaleInfo,
  plotMetrics: PlotMetrics
) {
  const cellWidth = plotMetrics.width / croppedValues.xBinCount
  const cellHeight = plotMetrics.height / croppedValues.yBinCount

  for (let y = 0; y < croppedValues.yBinCount; y++) {
    for (let x = 0; x < croppedValues.xBinCount; x++) {
      const index = y * croppedValues.xBinCount + x
      const value = displayValues[index]
      ctx.fillStyle = Number.isFinite(value)
        ? colorScale.colorScale.getColorForValue(value)
        : DEFAULT_MISSING_COLOR
      const left = Math.floor(plotMetrics.left + x * cellWidth)
      const right = Math.ceil(plotMetrics.left + (x + 1) * cellWidth)
      const top = Math.floor(
        plotMetrics.top + (croppedValues.yBinCount - 1 - y) * cellHeight
      )
      const bottom = Math.ceil(
        plotMetrics.top + (croppedValues.yBinCount - y) * cellHeight
      )
      ctx.fillRect(
        left,
        top,
        Math.max(1, right - left),
        Math.max(1, bottom - top)
      )
    }
  }
}

function drawPolarCircularCells(
  ctx: CanvasRenderingContext2D,
  croppedValues: CroppedValues,
  displayValues: Float32Array,
  colorScale: ColorScaleInfo,
  plotMetrics: PlotMetrics
) {
  const centerX = plotMetrics.left + plotMetrics.width / 2
  const centerY = plotMetrics.top + plotMetrics.height / 2
  const plotRadius = plotMetrics.width / 2
  const xStart = toDegrees(
    croppedValues.bounds.rect.x.min.toNumber(croppedValues.bounds.rect.x.unit),
    croppedValues.bounds.rect.x.unit
  )
  const xEnd = toDegrees(
    croppedValues.bounds.rect.x.max.toNumber(croppedValues.bounds.rect.x.unit),
    croppedValues.bounds.rect.x.unit
  )
  const yStart = croppedValues.bounds.rect.y.min.toNumber(
    croppedValues.bounds.rect.y.unit
  )
  const yEnd = croppedValues.bounds.rect.y.max.toNumber(
    croppedValues.bounds.rect.y.unit
  )
  const angleSpan = xEnd - xStart
  const radiusSpan = Math.max(yEnd - yStart, Number.EPSILON)

  for (let y = 0; y < croppedValues.yBinCount; y++) {
    const innerRadius =
      (plotRadius *
        (yStart + (y * radiusSpan) / croppedValues.yBinCount - yStart)) /
      radiusSpan
    const outerRadius =
      (plotRadius *
        (yStart + ((y + 1) * radiusSpan) / croppedValues.yBinCount - yStart)) /
      radiusSpan

    for (let x = 0; x < croppedValues.xBinCount; x++) {
      const index = y * croppedValues.xBinCount + x
      const value = displayValues[index]
      const angleStart = xStart + (x * angleSpan) / croppedValues.xBinCount
      const angleEnd = xStart + ((x + 1) * angleSpan) / croppedValues.xBinCount

      ctx.fillStyle = Number.isFinite(value)
        ? colorScale.colorScale.getColorForValue(value)
        : DEFAULT_MISSING_COLOR

      fillPolarSector(
        ctx,
        centerX,
        centerY,
        innerRadius,
        outerRadius,
        angleStart,
        angleEnd
      )
    }
  }
}

function renderLegendCanvas(
  request: ReportHeatmapRequest,
  colorScale: ColorScaleInfo
) {
  const canvas = createHtmlCanvas()
  const ctx = canvas.getContext('2d')

  if (!ctx) {
    throw new Error('Failed to create report legend canvas context')
  }

  canvas.width = LEGEND_WIDTH_PX
  canvas.height = LEGEND_HEIGHT_PX

  ctx.fillStyle = request.background === 'white' ? '#ffffff' : '#ffffff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  const gradientX = 24
  const titleX = gradientX
  const titleY = 16
  const titleLineHeight = 13
  const titleLines = wrapCanvasText(
    ctx,
    request.legendTitle ?? request.cdf.data.params.view.name,
    canvas.width - gradientX - 6
  )
  const gradientY =
    titleY + Math.max(titleLines.length, 1) * titleLineHeight + 10
  const gradientWidth = 30
  const gradientHeight = canvas.height - gradientY - 24
  const gradient = ctx.createLinearGradient(
    0,
    gradientY + gradientHeight,
    0,
    gradientY
  )

  for (let step = 0; step <= 1; step += 0.02) {
    const value = colorScale.min + (colorScale.max - colorScale.min) * step
    gradient.addColorStop(step, colorScale.colorScale.getColorForValue(value))
  }

  ctx.fillStyle = gradient
  ctx.fillRect(gradientX, gradientY, gradientWidth, gradientHeight)
  ctx.strokeStyle = '#1f1f1f'
  ctx.strokeRect(gradientX, gradientY, gradientWidth, gradientHeight)

  ctx.fillStyle = '#1f1f1f'
  ctx.font = 'bold 12px Arial'
  ctx.textAlign = 'left'
  ctx.textBaseline = 'alphabetic'
  titleLines.forEach((line, index) => {
    ctx.fillText(line, titleX, titleY + index * titleLineHeight)
  })
  ctx.font = '11px Arial'
  ctx.fillText(colorScale.measurementUnit.name, gradientX, canvas.height - 8)

  const ticks = [
    { label: formatTick(colorScale.max), value: colorScale.max },
    { label: formatTick(colorScale.mid), value: colorScale.mid },
    { label: formatTick(colorScale.min), value: colorScale.min },
  ]

  ticks.forEach((tick) => {
    const ratio =
      colorScale.max === colorScale.min
        ? 0.5
        : (tick.value - colorScale.min) / (colorScale.max - colorScale.min)
    const y = gradientY + gradientHeight - ratio * gradientHeight

    ctx.beginPath()
    ctx.moveTo(gradientX + gradientWidth, y)
    ctx.lineTo(gradientX + gradientWidth + 8, y)
    ctx.stroke()
    ctx.fillText(tick.label, gradientX + gradientWidth + 14, y + 4)
  })

  return canvas
}

function drawAxes(
  ctx: CanvasRenderingContext2D,
  plotMetrics: PlotMetrics,
  bounds: NormalizedBounds,
  request: ReportHeatmapRequest
) {
  const displayBounds = createDisplayBounds(bounds, {
    x: request.axisMetadata?.x?.displayUnit,
    y: request.axisMetadata?.y?.displayUnit,
  })
  const xStart = displayBounds.x.start
  const xEnd = displayBounds.x.end
  const yStart = displayBounds.y.start
  const yEnd = displayBounds.y.end
  const plotLeft = plotMetrics.left
  const plotTop = plotMetrics.top
  const plotRight = plotMetrics.right
  const plotBottom = plotMetrics.bottom
  const plotWidth = plotMetrics.width
  const plotHeight = plotMetrics.height

  ctx.strokeStyle = '#1f1f1f'
  ctx.fillStyle = '#1f1f1f'
  ctx.lineWidth = 1
  ctx.font = `${plotMetrics.axisFontSize ?? BASE_AXIS_FONT_SIZE_PX}px Arial`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'top'

  ctx.beginPath()
  ctx.moveTo(plotLeft, plotBottom)
  ctx.lineTo(plotRight, plotBottom)
  ctx.lineTo(plotRight, plotTop)
  ctx.stroke()

  const xDisplayUnit =
    request.axisMetadata?.x?.displayUnit ??
    resolveAxisDisplayUnit(bounds.rect.x.unit)
  const boilerTicks = getBoilerTubeAxisTicks(
    request.componentModel,
    xStart,
    xEnd,
    xDisplayUnit
  )
  const xTickCandidates = boilerTicks
    ? boilerTicks.map(({ tick, customerNumber }) => ({
        tick,
        label: formatAxisTick(customerNumber),
      }))
    : createTicksWithEndpoints(xStart, xEnd, 6).map((tick) => ({
        tick,
        label: formatAxisTick(
          getCustomerTubeNumber(request.componentModel, tick, xDisplayUnit) ??
            tick
        ),
      }))
  const uniqueXTickCandidates = boilerTicks
    ? xTickCandidates
    : xTickCandidates.filter(
        (candidate, index, candidates) =>
          candidates.findIndex(({ label }) => label === candidate.label) ===
          index
      )
  const xTickIndices = selectNonOverlappingTickIndices(
    uniqueXTickCandidates.map(
      ({ tick }) =>
        plotLeft +
        (xEnd === xStart ? 0 : ((tick - xStart) / (xEnd - xStart)) * plotWidth)
    ),
    uniqueXTickCandidates.map(({ label }) => ctx.measureText(label).width)
  )
  xTickIndices.forEach((index) => {
    const { tick, label } = uniqueXTickCandidates[index]
    const ratio = xEnd === xStart ? 0 : (tick - xStart) / (xEnd - xStart)
    const x = plotLeft + ratio * plotWidth
    ctx.beginPath()
    ctx.moveTo(x, plotBottom)
    ctx.lineTo(x, plotBottom + 6)
    ctx.stroke()
    ctx.fillText(label, x, plotBottom + 10)
  })

  ctx.textAlign = 'right'
  ctx.textBaseline = 'middle'
  const yTickCandidates = createTicksWithEndpoints(yStart, yEnd, 5)
    .map((tick) => ({ tick, label: formatAxisTick(tick) }))
    .filter(
      (candidate, index, candidates) =>
        candidates.findIndex(({ label }) => label === candidate.label) === index
    )
  const yTickIndices = selectNonOverlappingTickIndices(
    yTickCandidates.map(
      ({ tick }) =>
        plotBottom -
        (yEnd === yStart ? 0 : ((tick - yStart) / (yEnd - yStart)) * plotHeight)
    ),
    yTickCandidates.map(
      () => plotMetrics.axisFontSize ?? BASE_AXIS_FONT_SIZE_PX
    )
  )
  yTickIndices.forEach((index) => {
    const { tick, label } = yTickCandidates[index]
    const ratio = yEnd === yStart ? 0 : (tick - yStart) / (yEnd - yStart)
    const y = plotBottom - ratio * plotHeight
    ctx.beginPath()
    ctx.moveTo(plotLeft - 6, y)
    ctx.lineTo(plotLeft, y)
    ctx.stroke()
    ctx.fillText(label, plotLeft - 10, y)
  })

  const xTitle = formatAxisTitle(
    request.axisMetadata?.x?.name,
    request.axisMetadata?.x?.displayUnit
  )
  if (xTitle) {
    ctx.textAlign = 'center'
    ctx.textBaseline = 'top'
    ctx.fillText(
      xTitle,
      plotLeft + plotWidth / 2,
      plotBottom + (plotMetrics.axisFontSize ?? BASE_AXIS_FONT_SIZE_PX) * 2 + 10
    )
  }

  const yTitle = formatAxisTitle(
    request.axisMetadata?.y?.name,
    request.axisMetadata?.y?.displayUnit
  )
  if (yTitle) {
    const axisFontSize = plotMetrics.axisFontSize ?? BASE_AXIS_FONT_SIZE_PX
    ctx.save()
    ctx.translate(plotLeft - axisFontSize * 2 - 12, plotTop + plotHeight / 2)
    ctx.rotate(-Math.PI / 2)
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(yTitle, 0, 0)
    ctx.restore()
  }
}

function drawPolarCircularAxes(
  ctx: CanvasRenderingContext2D,
  plotMetrics: PlotMetrics,
  request: ReportHeatmapRequest,
  bounds: NormalizedBounds
) {
  const centerX = plotMetrics.left + plotMetrics.width / 2
  const centerY = plotMetrics.top + plotMetrics.height / 2
  const outerRadius = plotMetrics.width / 2

  ctx.strokeStyle = '#1f1f1f'
  ctx.fillStyle = '#1f1f1f'
  ctx.lineWidth = 2
  ctx.font = '12px Arial'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'

  ctx.beginPath()
  ctx.arc(centerX, centerY, outerRadius, 0, Math.PI * 2)
  ctx.stroke()

  if (!hasFullPolarSweep(bounds)) {
    return
  }

  const compassLabels = getCompassLabels(
    getCompassOrientationDegrees(request.componentModel),
    10
  )

  compassLabels.forEach(({ label, positionDegrees }) => {
    const tickStart = polarToCanvasPoint(
      centerX,
      centerY,
      outerRadius + POLAR_COMPASS_TICK_INSET_PX,
      positionDegrees
    )
    const tickEnd = polarToCanvasPoint(
      centerX,
      centerY,
      outerRadius + POLAR_COMPASS_TICK_INSET_PX + POLAR_COMPASS_TICK_LENGTH_PX,
      positionDegrees
    )

    ctx.beginPath()
    ctx.moveTo(tickStart.x, tickStart.y)
    ctx.lineTo(tickEnd.x, tickEnd.y)
    ctx.stroke()

    const labelPoint = polarToCanvasPoint(
      centerX,
      centerY,
      outerRadius +
        POLAR_COMPASS_TICK_INSET_PX +
        POLAR_COMPASS_TICK_LENGTH_PX +
        POLAR_COMPASS_LABEL_OFFSET_PX,
      positionDegrees
    )
    ctx.font = /^[NESW]$/.test(label) ? 'bold 13px Arial' : '12px Arial'
    ctx.fillText(label, labelPoint.x, labelPoint.y)
  })
}

function createPlotMetrics(
  canvas: HTMLCanvasElement,
  rightGutterWidth: number,
  axisLayout: CartesianAxisLayout
): PlotMetrics {
  const left = axisLayout.leftPadding
  const top = axisLayout.topPadding
  const gutterRight = canvas.width - axisLayout.horizontalPadding
  const gutterLeft = gutterRight - rightGutterWidth
  const right = gutterLeft
  const bottom = canvas.height - axisLayout.bottomPadding

  return {
    left,
    top,
    right,
    bottom,
    width: right - left,
    height: bottom - top,
    gutterLeft,
    gutterRight,
    bottomPadding: axisLayout.bottomPadding,
    axisFontSize: axisLayout.fontSize,
  }
}

function createCartesianAxisLayout(
  outputWidthPx: number,
  includeAxes: boolean,
  _ctx: CanvasRenderingContext2D
): CartesianAxisLayout {
  if (!includeAxes) {
    return {
      fontSize: BASE_AXIS_FONT_SIZE_PX,
      topPadding: HEATMAP_TOP_PADDING,
      bottomPadding: HEATMAP_BOTTOM_PADDING,
      leftPadding: HEATMAP_LEFT_PADDING,
      horizontalPadding: HEATMAP_HORIZONTAL_PADDING,
    }
  }

  const axisScale = Math.min(
    Math.max(outputWidthPx / BASE_AXIS_OUTPUT_WIDTH_PX, 1),
    MAX_AXIS_FONT_SIZE_PX / BASE_AXIS_FONT_SIZE_PX
  )

  const fontSize = Math.round(BASE_AXIS_FONT_SIZE_PX * axisScale)

  return {
    fontSize,
    topPadding: Math.round(HEATMAP_TOP_PADDING * axisScale),
    bottomPadding:
      Math.min(
        Math.round(HEATMAP_BOTTOM_PADDING * axisScale),
        MAX_AXIS_BOTTOM_PADDING_PX
      ) + AXIS_EDGE_PADDING_PX,
    leftPadding:
      Math.min(
        Math.round(HEATMAP_LEFT_PADDING * axisScale),
        MAX_AXIS_LEFT_PADDING_PX
      ) + AXIS_EDGE_PADDING_PX,
    horizontalPadding: Math.min(
      Math.max(
        Math.round(HEATMAP_HORIZONTAL_PADDING * axisScale),
        Math.round(fontSize * 2.5)
      ),
      MAX_AXIS_RIGHT_PADDING_PX
    ),
  }
}

function addCartesianTitlePadding(
  axisLayout: CartesianAxisLayout,
  plotHeight: number,
  ctx: CanvasRenderingContext2D,
  request: ReportHeatmapRequest
): CartesianAxisLayout {
  const yTitle = formatAxisTitle(
    request.axisMetadata?.y?.name,
    request.axisMetadata?.y?.displayUnit
  )
  if (!yTitle) {
    return axisLayout
  }

  ctx.font = `${axisLayout.fontSize}px Arial`
  const requiredHeight = Math.ceil(
    ctx.measureText(yTitle).width + axisLayout.fontSize * 2
  )
  const currentHeight =
    axisLayout.topPadding + plotHeight + axisLayout.bottomPadding
  const additionalPadding = Math.max(0, requiredHeight - currentHeight)
  const topPadding = Math.ceil(additionalPadding / 2)

  return {
    ...axisLayout,
    topPadding: axisLayout.topPadding + topPadding,
    bottomPadding: axisLayout.bottomPadding + additionalPadding - topPadding,
  }
}

function createPolarPlotMetrics(
  canvas: HTMLCanvasElement,
  plotDiameter: number
): PlotMetrics {
  const left = Math.round((canvas.width - plotDiameter) / 2)
  const top = Math.round((canvas.height - plotDiameter) / 2)
  const right = left + plotDiameter
  const bottom = top + plotDiameter

  return {
    left,
    top,
    right,
    bottom,
    width: plotDiameter,
    height: plotDiameter,
    gutterLeft: right,
    gutterRight: right,
  }
}

function fillPolarSector(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  innerRadius: number,
  outerRadius: number,
  angleStartDegrees: number,
  angleEndDegrees: number
) {
  const outerStart = toCanvasAngleRadians(angleStartDegrees)
  const outerEnd = toCanvasAngleRadians(angleEndDegrees)

  ctx.beginPath()
  if (innerRadius <= 0.5) {
    ctx.moveTo(centerX, centerY)
    ctx.arc(centerX, centerY, outerRadius, outerStart, outerEnd, true)
  } else {
    ctx.arc(centerX, centerY, outerRadius, outerStart, outerEnd, true)
    ctx.arc(centerX, centerY, innerRadius, outerEnd, outerStart, false)
  }
  ctx.closePath()
  ctx.fill()
}

function renderOverlayCanvas(
  request: ReportHeatmapRequest,
  bounds: NormalizedBounds,
  plotMetrics: PlotMetrics
) {
  const primitives = buildReportOverlayPrimitives(request, bounds)
  if (
    primitives.linePrimitives.length === 0 &&
    primitives.shapePrimitives.length === 0 &&
    primitives.labelPrimitives.length === 0
  ) {
    return undefined
  }

  const canvas = createHtmlCanvas()
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('Failed to create report overlay canvas context')
  }

  canvas.width = request.outputWidthPx
  canvas.height =
    plotMetrics.top +
    plotMetrics.height +
    (plotMetrics.bottomPadding ?? HEATMAP_BOTTOM_PADDING)

  renderReportOverlayCanvas(ctx, primitives, bounds, plotMetrics)
  return canvas
}

function createHtmlCanvas() {
  if (typeof document === 'undefined') {
    throw new Error('Report heatmap rendering requires a browser canvas')
  }

  return document.createElement('canvas')
}

async function canvasToBlob(canvas: HTMLCanvasElement) {
  if (canvas.toBlob) {
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/png')
    )
    if (blob) {
      return blob
    }
  }

  const dataUrl = canvas.toDataURL('image/png')
  const response = await fetch(dataUrl)
  return response.blob()
}
